import { useEffect, useRef } from 'react';

/**
 * A hand scanner types a whole code within a few milliseconds per key and ends it with Enter; a person types far
 * slower. Keys further apart than this start a new code, so a code typed by hand is never taken as scanned.
 */
export const WEDGE_GAP_MS = 60;
/**
 * A burst of at least WEDGE_MIN_RUN keys at scanner speed is a scanner's, never a person's. A later key or the Enter
 * within WEDGE_LATE_MS still belongs to it (a Bluetooth scanner can stall a key), so a scan is never cut in two.
 */
export const WEDGE_MIN_RUN = 3;
export const WEDGE_LATE_MS = 1000;
/**
 * The first keys of a read can stall too, before the burst is long enough to be known as a scanner's (round 36, Ravi:
 * after a pause, 'TEST-TESTBRAND01-BLACK-3XL' arrived as 'EST-TESTBRAND01-BLACK-3XL', refused as no item). A head of
 * fewer than WEDGE_MIN_RUN keys is kept for a burst that starts within WEDGE_LATE_MS of it. A person typing slowly never
 * makes a read of it: the burst after the head must still come at scanner speed, and a longer slow run starts over.
 */
const keepsHead = (text: string, gap: number) =>
  text !== '' && text.length < WEDGE_MIN_RUN && gap > WEDGE_GAP_MS && gap <= WEDGE_LATE_MS;
/** Reads that came while no screen took the hand scanner (a page still loading) wait this long for one. */
export const WEDGE_PENDING_MS = 15_000;

const isEditable = (target: EventTarget | null) => {
  const el = target as HTMLElement | null;
  return !!el && (el.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName));
};

/**
 * When a key was pressed, not when the page got round to it: a busy page (a load, a long render) hands over the keys
 * of one scan late and bunched, and timing them on arrival cut the scan in two (round 16, Sunita and Ravi: the head of
 * a code lost, or the whole read gone with the old tick still on screen).
 */
const keyTime = (e: KeyboardEvent): number => {
  const t = e.timeStamp;
  if (!t || !Number.isFinite(t)) return Date.now();
  // Old engines (and jsdom) give the epoch time; browsers give the time since the page's origin.
  if (t > 1e12) return t;
  const origin = typeof performance !== 'undefined' ? performance.timeOrigin : undefined;
  return origin ? origin + t : Date.now();
};

type Taker = { current: (code: string) => void };

/** When the read being handed over now came (its Enter): a read kept while the next screen loaded comes later. */
let handingAt: number | undefined;
/**
 * When the hand-scanner read a screen is handling now was read, for its double-fire window (round 33): a read kept
 * for the next screen is handed over later than it was read. Date.now() outside a hand-scanner read.
 */
export const handReadTime = () => handingAt ?? Date.now();
const handOver = (taker: Taker, code: string, at: number) => {
  handingAt = at;
  try {
    taker.current(code);
  } finally {
    handingAt = undefined;
  }
};

/**
 * One hand scanner for the whole Hub, listening from the first page load (installHandScanner): a scan is put together
 * key by key whatever screen is up or changing, then handed to every screen taking the hand scanner now. A scan read
 * while none is (the next screen still loading) waits WEDGE_PENDING_MS for one, never lost.
 */
const wedge = {
  installed: false,
  text: '',
  at: 0,
  /** Keys of this burst that came at scanner speed (WEDGE_GAP_MS apart). */
  run: 0,
  takers: [] as Array<Taker>,
  /** Scoped screens (useHandScanner with a binding) take keys themselves; while one is up nothing waits. */
  scoped: 0,
  pending: [] as Array<{ code: string; at: number }>,
  lostTimer: undefined as ReturnType<typeof setTimeout> | undefined,
};
const lostListeners = new Set<(fragment: string) => void>();

/**
 * Calls `listener` when a scan was cut off: a burst at scanner speed with no Enter (the read is not used). The screen
 * says so ("Scan not read…"), so no read ends without a word. Returns the unsubscribe.
 */
export const onLostHandScan = (listener: (fragment: string) => void) => {
  lostListeners.add(listener);
  return () => {
    lostListeners.delete(listener);
  };
};

const tellLost = (fragment: string) => lostListeners.forEach(listener => listener(fragment));

/** Event fired on window with the read when a sheet holding the scanner (`data-scan-hold`) took it. */
export const SCAN_HELD_EVENT = 'hub-scan-held';

/**
 * A sheet over the screen (the facility sheet) holds the hand scanner: a read then never reaches the page under it
 * unseen (round 19, Manoj: a read behind the sheet changed Quick Pick's shelf). The sheet says it was not used.
 */
const heldBySheet = (code: string): boolean => {
  if (typeof document === 'undefined' || !document.querySelector('[data-scan-hold]')) return false;
  window.dispatchEvent(new CustomEvent(SCAN_HELD_EVENT, { detail: code }));
  return true;
};

/** Hands a whole read to the screens taking it, or keeps it for the next one; false when a scoped screen owns it. */
const deliver = (code: string, at: number): boolean => {
  if (heldBySheet(code)) return true;
  if (wedge.takers.length > 0) {
    wedge.takers.slice().forEach(taker => handOver(taker, code, at));
    return true;
  }
  // A scoped screen took (or refused) the keys itself.
  if (wedge.scoped > 0) return false;
  wedge.pending = [...wedge.pending.filter(p => at - p.at < WEDGE_PENDING_MS), { code, at }];
  return true;
};

const armLost = () => {
  if (wedge.lostTimer) clearTimeout(wedge.lostTimer);
  wedge.lostTimer = undefined;
  if (wedge.run < WEDGE_MIN_RUN) return;
  const fragment = wedge.text;
  const at = wedge.at;
  wedge.lostTimer = setTimeout(() => {
    wedge.lostTimer = undefined;
    // Still the same burst and no Enter came: the scan was cut off.
    if (wedge.text === fragment && wedge.at === at) {
      wedge.text = '';
      wedge.run = 0;
      tellLost(fragment);
    }
  }, WEDGE_LATE_MS);
};

const onWedgeKey = (e: KeyboardEvent) => {
  if (e.ctrlKey || e.metaKey || e.altKey) return;
  // A text box takes its own keys (a typed scan, a damaged label).
  if (isEditable(e.target)) return;
  const now = keyTime(e);
  const gap = now - wedge.at;
  const scannerRun = wedge.text !== '' && gap >= 0 && gap <= WEDGE_GAP_MS;
  // A scan already seen at scanner speed keeps a late key or Enter: one scan, never two halves.
  const lateOfScan = wedge.text !== '' && wedge.run >= WEDGE_MIN_RUN && gap >= 0 && gap <= WEDGE_LATE_MS;
  if (e.key === 'Enter' || e.key === 'Tab') {
    const text = wedge.text;
    const take = text.length >= 2 && (scannerRun || lateOfScan);
    wedge.text = '';
    wedge.run = 0;
    wedge.at = 0;
    if (wedge.lostTimer) clearTimeout(wedge.lostTimer);
    wedge.lostTimer = undefined;
    // Taken (or kept for the next screen): the Enter is the scanner's, never a tap on a focused button.
    if (take && deliver(text, Date.now())) {
      e.preventDefault();
      e.stopPropagation();
    }
    return;
  }
  if (e.key.length !== 1) return;
  if (scannerRun || lateOfScan) {
    wedge.text += e.key;
    if (scannerRun) wedge.run += 1;
  } else if (keepsHead(wedge.text, gap)) {
    // A stalled first key or two: the read goes on with them, and its run starts again from this key.
    wedge.text += e.key;
    wedge.run = 1;
  } else {
    wedge.text = e.key;
    wedge.run = 1;
  }
  wedge.at = now;
  armLost();
};

/**
 * Starts the Hub-wide hand scanner (idempotent). The Hub calls it before its first render, so a scan fired while a
 * screen loads is caught whole; any screen using useHandScanner also starts it.
 */
export const installHandScanner = () => {
  if (wedge.installed || typeof window === 'undefined') return;
  wedge.installed = true;
  window.addEventListener('keydown', onWedgeKey, true);
};

/** Tests: a phone with nothing typed, nothing waiting and no screen listening. */
export const resetHandScanner = () => {
  if (typeof window !== 'undefined') window.removeEventListener('keydown', onWedgeKey, true);
  if (wedge.lostTimer) clearTimeout(wedge.lostTimer);
  Object.assign(wedge, {
    installed: false,
    text: '',
    at: 0,
    run: 0,
    takers: [],
    scoped: 0,
    pending: [],
    lostTimer: undefined,
  });
};

/** A screen takes the hand scanner: reads waiting for one (read while it loaded) are handed to it now. */
const take = (taker: Taker) => {
  installHandScanner();
  wedge.takers.push(taker);
  if (wedge.pending.length === 0) return () => drop(taker);
  // After this render: every screen mounting with it (a page and its scan box) is listening by then.
  const timer = setTimeout(() => {
    const now = Date.now();
    const waiting = wedge.pending.filter(p => now - p.at < WEDGE_PENDING_MS);
    wedge.pending = [];
    if (wedge.takers.length === 0) {
      wedge.pending = waiting;
      return;
    }
    waiting.forEach(p => wedge.takers.slice().forEach(t => handOver(t, p.code, p.at)));
  }, 0);
  return () => {
    clearTimeout(timer);
    drop(taker);
  };
};

const drop = (taker: Taker) => {
  const at = wedge.takers.indexOf(taker);
  if (at >= 0) wedge.takers.splice(at, 1);
};

/**
 * Catches a hand scanner outside a text box. Optional binding freezes the work/field at the first character;
 * a changed binding or scoped disable discards that burst, including its tail, until its terminator or a new gap.
 */
export const useHandScanner = (
  onCode: (code: string) => void,
  enabled = true,
  getBurstBinding?: () => string | undefined,
) => {
  const onCodeRef = useRef(onCode);
  onCodeRef.current = onCode;
  const bindingRef = useRef(getBurstBinding);
  bindingRef.current = getBurstBinding;
  const enabledRef = useRef(enabled);
  const buffer = useRef<{
    text: string;
    at: number;
    binding?: string;
    discarded?: boolean;
    length?: number;
  }>({
    text: '',
    at: 0,
  });
  const scoped = Boolean(getBurstBinding);
  if (scoped && enabledRef.current && !enabled && (buffer.current.text || buffer.current.discarded))
    buffer.current.discarded = true;
  enabledRef.current = enabled;
  // Scoped disabled work observes framing without callbacks; only an earlier owned burst's terminator is consumed.
  // This prevents a tail being accepted after re-enable while preserving ordinary SKU/typed input behavior.
  // Two-argument callers keep their existing listener behavior.
  const listening = scoped || enabled;

  // Unscoped: the Hub-wide hand scanner hands this screen every whole read while it is enabled.
  useEffect(() => {
    if (scoped || !enabled) return;
    return take(onCodeRef);
  }, [scoped, enabled]);
  useEffect(() => {
    if (!scoped) return;
    installHandScanner();
    wedge.scoped += 1;
    return () => {
      wedge.scoped -= 1;
    };
  }, [scoped]);

  useEffect(() => {
    if (!listening || !scoped) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      const b = buffer.current;
      if (isEditable(e.target)) {
        if (b.text || b.discarded) {
          if (e.key === 'Enter' || e.key === 'Tab') buffer.current = { text: '', at: 0 };
          else if (e.key.length === 1) {
            b.discarded = true;
            b.at = Date.now();
          }
        }
        return;
      }
      const now = Date.now();
      const binding = bindingRef.current?.();
      const allowed = enabledRef.current && binding !== undefined;
      const fresh = Boolean(b.text || b.discarded) && now - b.at <= WEDGE_GAP_MS;
      if (e.key === 'Enter' || e.key === 'Tab') {
        buffer.current = { text: '', at: 0 };
        const recognized = fresh && (b.length ?? b.text.length) >= 2;
        if (recognized && (b.binding !== undefined || allowed)) {
          // A rejected owned burst must not turn Enter/Tab into Confirm or another native action.
          e.preventDefault();
          e.stopPropagation();
        }
        if (recognized && !b.discarded && allowed && b.binding === binding && !heldBySheet(b.text))
          onCodeRef.current(b.text);
        return;
      }
      if (e.key.length !== 1) return;
      // A stalled first key or two (see keepsHead) stay the head of this read.
      const head =
        !fresh && !b.discarded && allowed && b.binding === binding && keepsHead(b.text, now - b.at);
      if (head) {
        b.length = (b.length ?? b.text.length) + 1;
        b.text += e.key;
        b.at = now;
      } else if (!fresh) {
        buffer.current = {
          text: allowed ? e.key : '',
          at: now,
          binding,
          discarded: !allowed,
          length: 1,
        };
      } else {
        b.length = (b.length ?? b.text.length) + 1;
        b.discarded = b.discarded || !allowed || b.binding !== binding;
        if (!b.discarded) b.text += e.key;
        b.at = now;
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [listening, scoped]);
};
